import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { deleteApiExchangeByEncryptedOrderIdMutation } from 'api/generated/@tanstack/react-query.gen';
import { useAppState } from 'lib/app-state';

/**
 * Drops a ticket change in progress: the exchange leaves app state and its
 * staging order stops being the active order, so the original tickets stay as
 * they were and nothing is charged.
 *
 * The change is dropped server-side too, releasing the seats the staging order
 * holds instead of leaving them to expire. The call is keyed on the original
 * order and succeeds even before a staging order exists, so it is made for
 * every cancellation. It is not awaited and its failure is not surfaced: the
 * buyer is on their way out of the flow, and the seats fall back to expiring on
 * their own.
 */
export function useCancelExchange() {
  const {
    tempExchangeData,
    setTempExchangeData,
    setEventData
  } = useAppState();
  const {
    mutate: dropExchange
  } = useMutation(deleteApiExchangeByEncryptedOrderIdMutation());
  return useCallback(() => {
    const sourceOrderId = tempExchangeData?.sourceEncryptedOrderId;
    const exchangeOrderId = tempExchangeData?.exchangeOrderId;
    setTempExchangeData(null);
    // Only the staging order goes with the change. Before one exists the order
    // in state is the buyer's own and stays untouched.
    if (exchangeOrderId) {
      setEventData(prev => prev.order?.encryptedId === exchangeOrderId ? {
        event: undefined,
        order: undefined
      } : prev);
    }
    if (sourceOrderId) {
      dropExchange({
        path: {
          encryptedOrderId: sourceOrderId
        }
      });
    }
  }, [dropExchange, setEventData, setTempExchangeData, tempExchangeData]);
}